import React, { useState, useEffect } from "react";
import "./About.css";
import "./Card.css";
import "./Careers.css"; // Utilizing the Premium DesignSpace template CSS
import "./FxFeatureCard.css";
import "./FxFeatures.css";
import "./Facilities.css";
import "./HighlightsCube.css";
import { API_BASE_URL } from "../config";
import AboutStats from "./AboutStats";

const ABOUT_API = `${API_BASE_URL}/api/about`;

/* ─── Default fallbacks (shown while loading or if DB is empty) ─────────── */
const DEFAULT_HERO_IMAGES = [
  { src: "/service1.webp", alt: "Drone Training" },
  { src: "/IMG_20250516_112635886.webp", alt: "Simulator Training" },
  { src: "/service3.webp", alt: "Hands-on Flying" },
];

const DEFAULT_CORE_VALUES = [
  { icon: "🚀", title: "Innovation", description: "At Flytutor, innovation drives everything we do. We combine cutting-edge drone technology with immersive learning experiences." },
  { icon: "🤝", title: "Collaboration", description: "Collaboration is where ideas take flight. By partnering with industry leaders, we create limitless learning opportunities." },
  { icon: "💡", title: "Creativity", description: "Creativity is our lift-off. At Flytutor, we blend imagination with advanced technology to craft unique learning experiences." },
  { icon: "🛡️", title: "Excellence", description: "We meticulously craft scalable curriculums, ensuring our students are industry-ready and prepared to pilot future innovations." },
];

const DEFAULT_HIGHLIGHTS = [
  { title: "Excellence", text: "DGCA-authorized training delivering industry-ready drone pilots with uncompromising quality standards.", imageUrl: "/images/highlights/excellence.png" },
  { title: "Innovation", text: "Hands-on UAV programs integrating simulation, field practice, and advanced technologies.", imageUrl: "/images/highlights/innovation.png" },
  { title: "Integrity", text: "Safety-focused drone education ensuring compliance, responsibility, and professional standards.", imageUrl: "/images/highlights/integrity.png" },
  { title: "Collaboration", text: "Strategic industry partnerships strengthening exposure to real-world drone ecosystems.", imageUrl: "/images/highlights/collaboration.png" },
  { title: "Expertise", text: "Instructor-led training backed by years of professional UAV experience.", imageUrl: "/images/highlights/expertise.png" },
  { title: "Technology", text: "Advanced drones and simulators powering future-ready pilot education.", imageUrl: "/images/highlights/technology.png" },
];

const DEFAULT_FACILITIES_IMAGES = [
  "/service3.webp",
  "/IMG_20250516_112635886.webp",
  "/service1.webp",
  "/Add on Course Images/IMG_6269.jpeg",
  "/greenery_acre.webp",
  "/hostel.webp",
];
const DEFAULT_FACILITIES = [
  { title: "Drone Training", tag: "Core UAV Program", points: ["DGCA aligned curriculum", "Basic to advanced UAV flying", "Aerial safety & airspace rules", "Industry-grade training drones"] },
  { title: "Simulator Training", tag: "Skill & Safety Builder", points: ["Realistic flight simulation", "Emergency handling practice", "Weather-based flying scenarios", "Pre-flight confidence training"] },
  { title: "Hands-on Flying", tag: "Real World Experience", points: ["Outdoor flying sessions", "Instructor-guided missions", "Live troubleshooting practice", "Confidence & accuracy drills"] },
  { title: "Modern Classrooms", tag: "Smart Learning Hub", points: ["Air-conditioned digital classrooms", "High-resolution projection displays", "Interactive theory sessions", "Expert aviation instructors"] },
  { title: "24-Acre Flying Zone", tag: "Exclusive Flight Arena", points: ["Dedicated 24-acre open airspace", "DGCA-compliant flying grounds", "Multiple terrain environments", "All-weather operational zones"] },
  { title: "Premium Hostel", tag: "Residential Campus", points: ["Fully furnished accommodation", "24/7 campus security", "High-speed Wi-Fi connectivity", "Nutritious mess & dining hall"] },
];

const DEFAULT_CONFIG = {
  heroParagraph: "Flytutor is pioneering the future of drone education, making advanced aerial technology and UAV training accessible to everyone. Founded by aviation experts and technology innovators, we offer comprehensive programs that combine cutting-edge knowledge with hands-on flight experience.",
  fxFeature1Icon: "🎯", fxFeature1Title: "Expert Training", fxFeature1Desc: "Industry-leading drone training curriculum designed by aviation professionals to provide hands-on expertise, advanced UAV skills, and career-ready knowledge.",
  fxFeature2Icon: "🛡️", fxFeature2Title: "Safety First", fxFeature2Desc: "Comprehensive drone safety protocols and industry best practices to ensure secure, professional, and risk-free UAV operations.",
  fxFeature3Icon: "🚀", fxFeature3Title: "Innovation", fxFeature3Desc: "Cutting-edge drone technology and advanced training methods for hands-on learning, UAV mastery, and industry-ready skills.",
  coreValuesBottomHeading: "Your Flight Journey Starts with Expert Support.",
  coreValuesBottomParagraph: "Step into the welcoming embrace of Flytutor, where our expert instruction team awaits to guide you towards earning your DGCA certification.",
  dgcaSectionHeading: "Approved by DGCA",
  dgcaApprovalText: "Our commitment to excellence has been recognized by leading industry bodies, with certificates validating our dedication to quality and innovation. These recognitions reflect our promise to deliver excellence to our students.",
  ctaHeading: "Ready to Take Flight?",
  ctaParagraph: "Join hundreds of successful drone pilots who launched their careers with Flytutor, gaining expert training and DGCA-approved certification.",
};

const AboutUs = () => {
  const [collaborators, setCollaborators] = useState([]);
  const [stats, setStats] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Dynamic data from API
  const [heroImages, setHeroImages] = useState([]);
  const [coreValues, setCoreValues] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [dgcaCerts, setDgcaCerts] = useState([]);
  const [config, setConfig] = useState(DEFAULT_CONFIG);

  // Fetch all about page dynamic content
  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        const [hi, cv, hl, fac, dg, cfg] = await Promise.all([
          fetch(`${ABOUT_API}/hero-images`).then((r) => r.json()).catch(() => []),
          fetch(`${ABOUT_API}/core-values`).then((r) => r.json()).catch(() => []),
          fetch(`${ABOUT_API}/highlights`).then((r) => r.json()).catch(() => []),
          fetch(`${ABOUT_API}/facilities`).then((r) => r.json()).catch(() => []),
          fetch(`${ABOUT_API}/dgca-certs`).then((r) => r.json()).catch(() => []),
          fetch(`${ABOUT_API}/config`).then((r) => r.json()).catch(() => ({})),
        ]);
        setHeroImages(Array.isArray(hi) && hi.length > 0 ? hi : []);
        setCoreValues(Array.isArray(cv) && cv.length > 0 ? cv : []);
        setHighlights(Array.isArray(hl) && hl.length > 0 ? hl : []);
        setFacilities(Array.isArray(fac) && fac.length > 0 ? fac : []);
        setDgcaCerts(Array.isArray(dg) ? dg : []);
        if (cfg && cfg._id) setConfig({ ...DEFAULT_CONFIG, ...cfg });
      } catch (err) {
        console.error("About page data fetch error:", err);
      }
    };
    fetchAboutData();
  }, []);

  // Collaborators
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/collaborators`)
      .then((res) => res.json())
      .then((data) => setCollaborators(Array.isArray(data) ? data : []))
      .catch((err) => console.error("Collaborators fetch error:", err));
  }, []);

  // Stats
  useEffect(() => {
    fetch("https://siteapi.flytutor.in/api/stats")
      .then((res) => res.json())
      .then((data) => setStats(data))
      .catch((err) => console.error("Stats Fetch Error:", err));
  }, []);

  const activeHeroImages = heroImages.length > 0 ? heroImages : DEFAULT_HERO_IMAGES;
  
  const activeCoreValues = Array.from(
    { length: Math.max(DEFAULT_CORE_VALUES.length, coreValues.length) },
    (_, i) => coreValues[i] || DEFAULT_CORE_VALUES[i % DEFAULT_CORE_VALUES.length]
  );
  
  const activeHighlights = Array.from(
    { length: Math.max(DEFAULT_HIGHLIGHTS.length, highlights.length) },
    (_, i) => highlights[i] || DEFAULT_HIGHLIGHTS[i % DEFAULT_HIGHLIGHTS.length]
  );
  
  const activeFacilities = Array.from(
    { length: Math.max(DEFAULT_FACILITIES.length, facilities.length) },
    (_, i) => facilities[i] || DEFAULT_FACILITIES[i % DEFAULT_FACILITIES.length]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % activeHeroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [activeHeroImages.length]);

  return (
    <div className="about-us-container" style={{ background: '#fff' }}>

      {/* SECTION 1: Hero (White) */}
      <section className="about-white-section">
        <div className="careers-hero" style={{ padding: '0' }}>
          <div className="ch-left">
            <h1>
              About <br />
              <span>Flytutor</span>
            </h1>
            <p className="ch-desc" style={{ marginBottom: "35px", maxWidth: "550px" }}>
              {config.heroParagraph}
            </p>
          </div>

          <div className="about-hero-slider">
            {activeHeroImages.map((img, idx) => {
              const bgUrl = img.url 
                ? (img.url.startsWith("/uploads/") ? `${API_BASE_URL}${img.url}` : img.url)
                : (img.src || DEFAULT_HERO_IMAGES[idx % DEFAULT_HERO_IMAGES.length].src);
              return (
                <div
                  key={idx}
                  className={`slider-img ${idx === currentIndex ? 'active' : ''}`}
                  style={{ backgroundImage: `url('${bgUrl}')` }}
                />
              );
            })}
            <div className="slider-dots">
              {activeHeroImages.map((_, idx) => (
                <span
                  key={idx}
                  className={`dot ${idx === currentIndex ? 'active' : ''}`}
                  onClick={() => setCurrentIndex(idx)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: Features & Stats (Gray) */}
      <section className="about-gray-section">
        {/* FxFeatures — dynamic */}
        <section className="fx-features-section">
          <div className="fx-features-grid">
            {[1, 2, 3].map((n) => (
              <div key={n} className="fx-flip-container">
                <div className="fx-flip-card">
                  <div className="fx-flip-icon">{config[`fxFeature${n}Icon`]}</div>
                  <p className="fx-flip-heading">{config[`fxFeature${n}Title`]}</p>
                  <p className="fx-flip-text">{config[`fxFeature${n}Desc`]}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
        <AboutStats stats={stats} />
      </section>

      {/* SECTION 3: Core Values (White) */}
      <section className="about-white-section">
        <div className="careers-values-wrap" style={{ background: 'none', padding: '0' }}>
          <div className="cv-card" style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.05)' }}>
            {activeCoreValues.map((val, i) => (
              <div className="cv-item" key={i}>
                <div className="cv-icon">{val.icon}</div>
                <h4>{val.title}</h4>
                <p>{val.description}</p>
              </div>
            ))}
          </div>

          <div className="cv-bottom-msg">
            <h2 className="flytutor-main-heading" style={{ marginBottom: '20px' }}>
              {config.coreValuesBottomHeading}
            </h2>
            <p>{config.coreValuesBottomParagraph}</p>
          </div>
        </div>
      </section>

      {/* SECTION 4: Our Highlights (Gray) */}
      <section className="about-gray-section" style={{ padding: '0 !important' }}>
        <section className="hl-section">
          <div className="hl-header" style={{ marginBottom: '40px' }}>
            <h2 className="flytutor-main-heading" style={{ marginBottom: '15px' }}>Our Highlights</h2>
            <p className="hl-subtitle">Discover the pillars of our excellence in aviation training.</p>
          </div>
          <div className="hl-grid">
            {activeHighlights.map((item, i) => {
              let bgImg = "";
              if (item.imageUrl) {
                bgImg = item.imageUrl.startsWith("/uploads/") ? `${API_BASE_URL}${item.imageUrl}` : item.imageUrl;
              } else {
                bgImg = DEFAULT_HIGHLIGHTS[i % DEFAULT_HIGHLIGHTS.length]?.imageUrl || "";
              }
              return (
                <div className="hl-card" key={i}>
                  <div className="hl-img" style={{ backgroundImage: bgImg ? `url('${bgImg}')` : "none" }} />
                  <div className="hl-content">
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </section>

      {/* SECTION 5: DGCA Approval (White) */}
      <section className="about-white-section">
        <div className="approval-section" style={{ background: 'none', padding: '0' }}>
          <div className="section-header" style={{ marginBottom: '30px' }}>
            <h2 className="flytutor-main-heading">{config.dgcaSectionHeading}</h2>
          </div>

          <div className="approval-container">
            <div className="certificates-wrapper">
              {dgcaCerts.length > 0
                ? dgcaCerts.map((cert, i) => (
                    <div key={cert._id} className="certificate-card" style={{ "--delay": `${i * 0.3}s` }}>
                      <div className="certificate-frame">
                        <div className="certificate-seal">✓</div>
                        <img
                          src={`${API_BASE_URL}${cert.imageUrl}`}
                          alt={cert.alt || "DGCA Certificate"}
                          className="certificate-image"
                        />
                      </div>
                    </div>
                  ))
                : (
                    <>
                      <div className="certificate-card" style={{ "--delay": "0s" }}>
                        <div className="certificate-frame">
                          <div className="certificate-seal">✓</div>
                          <img src="/images/approval/certificate1.jpeg" alt="Certificate of Excellence" className="certificate-image" />
                        </div>
                      </div>
                      <div className="certificate-card" style={{ "--delay": "0.3s" }}>
                        <div className="certificate-frame">
                          <div className="certificate-seal">✓</div>
                          <img src="/images/approval/certificate2.jpeg" alt="Quality Assurance Certificate" className="certificate-image" />
                        </div>
                      </div>
                    </>
                  )}
            </div>

            <div className="approval-text" style={{ marginTop: '20px' }}>
              <div className="approval-icon">🏅</div>
              <h3>Industry Recognized</h3>
              <p>{config.dgcaApprovalText}</p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: Facilities (Gray) */}
      <section className="about-gray-section">
        <section className="facility-section">
          <h2 className="flytutor-main-heading">Our Training Facilities</h2>
          <div className="facility-stack">
            {activeFacilities.map((item, i) => {
              let imgSrc = "";
              if (item.imageUrl) {
                imgSrc = item.imageUrl.startsWith("/uploads/") ? `${API_BASE_URL}${item.imageUrl}` : item.imageUrl;
              } else {
                imgSrc = DEFAULT_FACILITIES_IMAGES[i % DEFAULT_FACILITIES_IMAGES.length];
              }
              return (
                <div className="facility-card" key={i}>
                  <img src={imgSrc} alt={item.title} />
                  <span className="facility-tag">{item.tag}</span>
                  <h3>{item.title}</h3>
                  <ul>
                    {item.points.map((p, idx) => (
                      <li key={idx}>{p}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </section>
      </section>

      {/* SECTION 7: Collaborations (White) */}
      <section className="about-white-section">
        <h2 className="flytutor-main-heading">Our Collaborations</h2>
        <div className="collaboration-slider">
          <div className="slider-track">
            {Array.isArray(collaborators) &&
              [...collaborators, ...collaborators].map((collaborator, index) => (
                <div key={index} className="collaboration-logo">
                  <div className="logo-container">
                    <img
                      src={`${API_BASE_URL}${collaborator.url}`}
                      alt={collaborator.filename || "Partner Logo"}
                      loading="lazy"
                    />
                  </div>
                </div>
              ))}
          </div>
        </div>
      </section>

      {/* SECTION 8: CTA (Gray) */}
      <section className="about-gray-section">
        <div className="cta-container">
          <div className="cta-text">
            <h3>{config.ctaHeading}</h3>
            <p>{config.ctaParagraph}</p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default AboutUs;
